import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNotify } from 'react-admin';
import { EMPTY_PERMISSIONS } from '../types';
import {
  getIdToken,
  getSessionGeneration,
  set403Callback,
  subscribeSession,
} from '../providers/cognito';

const FORBIDDEN_MESSAGE =
  "You don't have permission for that action. Ask an administrator to assign you a role.";

export const ForbiddenWatcher = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();

  const failClosedAndRefreshPermissions = useCallback(() => {
    const generation = getSessionGeneration();
    const token = getIdToken();
    const query = { queryKey: ['auth', 'getPermissions'] } as const;
    const cancellation = queryClient.cancelQueries(query);

    // React Admin otherwise retains the previous privilege set while the
    // invalidated query refetches. Replace every permissions cache entry
    // immediately so menus and controls fail closed during that interval.
    queryClient.setQueriesData(query, EMPTY_PERMISSIONS);

    if (!token) return;
    void cancellation
      .then(() => {
        if (getSessionGeneration() !== generation || getIdToken() !== token) return;
        return queryClient.invalidateQueries({ ...query, refetchType: 'active' });
      })
      .catch(() => {
        // The empty permission cache is intentionally retained when the
        // authoritative refresh cannot complete.
      });
  }, [queryClient]);

  useEffect(() => {
    const unsubscribe = subscribeSession(failClosedAndRefreshPermissions);
    set403Callback(() => {
      failClosedAndRefreshPermissions();
      notify(FORBIDDEN_MESSAGE, { type: 'warning' });
    });
    return () => {
      unsubscribe();
      set403Callback(() => {});
    };
  }, [failClosedAndRefreshPermissions, notify]);

  return null;
};
